/**
 * MineCanvas.ts — the animated mine-shaft visualization.
 *
 * Renders stacked ore layers down to the unlocked depth, highlights the focused
 * layer with an animated drill, embedded glowing gem crystals and little miners,
 * and emits ore particles on click. All drawing is guarded so it safely no-ops
 * when no 2D context is available (e.g. under jsdom in tests).
 */
import type { GameState } from '../core/GameState';
import type { Derived } from '../systems/derived';
import { ORES, ORE_BY_DEPTH } from '../data';
import { Particles } from './particles';

/** Get a 2D context, tolerating environments (jsdom) where getContext throws. */
function safeContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
  } catch {
    return null;
  }
}

interface Digger {
  phase: number;
  speed: number;
  x: number;
}
interface Dust {
  x: number;
  y: number;
  vy: number;
  size: number;
  alpha: number;
}
interface Ring {
  x: number;
  y: number;
  r: number;
  max: number;
  color: string;
}

/** Deterministic pseudo-random in [0,1) from an integer seed. */
function hash(n: number): number {
  return ((Math.sin(n * 127.1 + 311.7) * 43758.5453) % 1 + 1) % 1;
}

export class MineCanvas {
  private ctx: CanvasRenderingContext2D | null;
  private particles = new Particles();
  private diggers: Digger[] = [];
  private dust: Dust[] = [];
  private farDust: Dust[] = [];
  private rings: Ring[] = [];
  private drips: Array<{ x: number; y: number; vy: number; floor: number }> = [];
  private cart = { y: 0, dir: 1, active: false, nextAt: 4 };
  private displayDepth = 0; // eased toward focusDepth for smooth highlight
  private t = 0;
  private w = 0;
  private h = 0;
  private dpr = 1;
  /** Set from settings — suppresses animated ambiance when true. */
  reducedMotion = false;
  /** Particle density multiplier (0..1) from settings. */
  density = 1;

  constructor(
    private canvas: HTMLCanvasElement,
    private getState: () => GameState,
    private getDerived: () => Derived,
  ) {
    this.ctx = safeContext(canvas);
    for (let i = 0; i < 4; i++) {
      this.diggers.push({ phase: Math.random() * Math.PI * 2, speed: 0.8 + Math.random() * 0.8, x: Math.random() });
    }
    this.displayDepth = this.getState().mining.focusDepth;
    this.resize();
  }

  private seedDust(): void {
    const n = Math.round((this.w * this.h) / 11000);
    this.dust = [];
    for (let i = 0; i < n; i++) {
      this.dust.push({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        vy: 3 + Math.random() * 9,
        size: 0.6 + Math.random() * 1.6,
        alpha: 0.04 + Math.random() * 0.14,
      });
    }
    // Far parallax layer: larger, slower, fainter motes drifting sideways.
    this.farDust = [];
    for (let i = 0; i < Math.round(n * 0.45); i++) {
      this.farDust.push({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        vy: 1 + Math.random() * 3,
        size: 1.6 + Math.random() * 2.4,
        alpha: 0.03 + Math.random() * 0.06,
      });
    }
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
    this.w = Math.max(1, rect.width || this.canvas.width || 300);
    this.h = Math.max(1, rect.height || this.canvas.height || 400);
    this.canvas.width = Math.floor(this.w * this.dpr);
    this.canvas.height = Math.floor(this.h * this.dpr);
    if (this.ctx) this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.seedDust();
  }

  /** Emit an ore burst near the focused layer (called on click). */
  burstAt(clientX?: number, clientY?: number): void {
    const s = this.getState();
    const ore = ORE_BY_DEPTH[s.mining.focusDepth] ?? ORES[0]!;
    const x = clientX ?? this.w / 2;
    const y = clientY ?? this.focusBandY();
    this.particles.burst(x, y, ore.color, 18);
    this.rings.push({ x, y, r: 2, max: 52, color: ore.color });
    if (this.rings.length > 18) this.rings.shift();
  }

  private focusBandY(): number {
    const s = this.getState();
    const layers = s.mining.maxDepth + 1;
    const bandH = this.h / Math.max(1, layers);
    return bandH * (s.mining.focusDepth + 0.5);
  }

  render(dt: number): void {
    this.t += dt;
    this.particles.update(dt);

    const s = this.getState();
    const d = this.getDerived();
    this.displayDepth += (s.mining.focusDepth - this.displayDepth) * Math.min(1, dt * 8);

    const anim = !this.reducedMotion;
    for (const p of this.dust) {
      p.y += p.vy * dt;
      if (p.y > this.h) { p.y = -2; p.x = Math.random() * this.w; }
    }
    for (const p of this.farDust) {
      p.y += p.vy * dt;
      p.x += Math.sin(this.t * 0.3 + p.y) * 4 * dt;
      if (p.y > this.h) { p.y = -2; p.x = Math.random() * this.w; }
    }
    for (const r of this.rings) r.r += (r.max - r.r) * Math.min(1, dt * 7);
    this.rings = this.rings.filter((r) => r.max - r.r > 1.5);
    if (anim) { this.updateCart(dt, s.mining.maxDepth); this.updateDrips(dt, s.mining.maxDepth); }

    const ctx = this.ctx;
    if (!ctx) return;

    const W = this.w;
    const H = this.h;
    const active = d.miningRate > 0;

    // Backdrop.
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#1b2740');
    bg.addColorStop(0.1, '#141d2e');
    bg.addColorStop(1, '#05070c');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    const layers = s.mining.maxDepth + 1;
    const bandH = H / Math.max(1, layers);

    for (let depth = 0; depth <= s.mining.maxDepth; depth++) {
      const ore = ORE_BY_DEPTH[depth];
      if (!ore) continue;
      const y = depth * bandH;
      const focused = depth === s.mining.focusDepth;
      const glow = Math.max(0, 1 - Math.abs(depth - this.displayDepth));

      // Rock fill with vertical sheen + a soft focus glow.
      const grad = ctx.createLinearGradient(0, y, 0, y + bandH);
      grad.addColorStop(0, this.shade(ore.color, 0.24 + glow * 0.16));
      grad.addColorStop(0.5, this.shade(ore.color, 0.36 + glow * 0.3));
      grad.addColorStop(1, this.shade(ore.color, 0.18 + glow * 0.12));
      ctx.fillStyle = grad;
      ctx.fillRect(0, y, W, bandH);

      this.drawRock(ctx, depth, y, bandH, ore.color, glow);
      this.drawVeins(ctx, depth, y, bandH, ore.color, glow);
      if (glow > 0.2) this.drawGems(ctx, depth, y, bandH, ore.color, glow);

      // Divider strata line with a highlight.
      ctx.strokeStyle = 'rgba(0,0,0,0.45)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y + bandH);
      ctx.lineTo(W, y + bandH);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,255,255,0.04)';
      ctx.beginPath();
      ctx.moveTo(0, y + bandH + 1);
      ctx.lineTo(W, y + bandH + 1);
      ctx.stroke();

      this.drawLabel(ctx, ore.icon, ore.name, y + 18, focused);

      if (focused) {
        ctx.strokeStyle = `rgba(255,182,72,${0.5 + 0.35 * Math.sin(this.t * 3)})`;
        ctx.lineWidth = 2;
        ctx.strokeRect(1.5, y + 1.5, W - 3, bandH - 3);
        this.drawDrill(ctx, y, bandH, active, ore.color);
        this.drawDiggers(ctx, y, bandH, active);
      }
    }

    // Godray: a soft light beam shining down the shaft.
    const shaftX0 = W / 2;
    const flick = anim ? 0.85 + 0.15 * Math.sin(this.t * 2.3) : 0.85;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const ray = ctx.createLinearGradient(0, 0, 0, H * 0.85);
    ray.addColorStop(0, `rgba(255, 236, 190, ${0.16 * flick})`);
    ray.addColorStop(1, 'rgba(255, 236, 190, 0)');
    ctx.fillStyle = ray;
    ctx.beginPath();
    ctx.moveTo(shaftX0 - 10, 0);
    ctx.lineTo(shaftX0 + 10, 0);
    ctx.lineTo(shaftX0 + 46, H * 0.85);
    ctx.lineTo(shaftX0 - 46, H * 0.85);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Central shaft with rails.
    const shaftX = W / 2;
    const shaftBottom = (s.mining.maxDepth + 1) * bandH;
    const shaftGrad = ctx.createLinearGradient(shaftX - 14, 0, shaftX + 14, 0);
    shaftGrad.addColorStop(0, 'rgba(0,0,0,0.05)');
    shaftGrad.addColorStop(0.5, 'rgba(0,0,0,0.32)');
    shaftGrad.addColorStop(1, 'rgba(0,0,0,0.05)');
    ctx.fillStyle = shaftGrad;
    ctx.fillRect(shaftX - 14, 0, 28, shaftBottom);
    ctx.strokeStyle = 'rgba(150,170,200,0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(shaftX - 11, 0); ctx.lineTo(shaftX - 11, shaftBottom);
    ctx.moveTo(shaftX + 11, 0); ctx.lineTo(shaftX + 11, shaftBottom); ctx.stroke();
    // Rail ties.
    ctx.strokeStyle = 'rgba(120,140,170,0.10)';
    for (let ty = 14; ty < shaftBottom; ty += 22) {
      ctx.beginPath(); ctx.moveTo(shaftX - 11, ty); ctx.lineTo(shaftX + 11, ty); ctx.stroke();
    }

    // Minecart riding the rails + water drips.
    if (this.cart.active) this.drawCart(ctx, shaftX, this.cart.y);
    this.drawDrips(ctx);

    // Dust motes (far parallax layer first, then near).
    for (const p of this.farDust) {
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = '#aebed6';
      ctx.fillRect(p.x, p.y, p.size, p.size);
    }
    for (const p of this.dust) {
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = '#cdd9ec';
      ctx.fillRect(p.x, p.y, p.size, p.size);
    }
    ctx.globalAlpha = 1;

    // Click shockwave rings.
    for (const r of this.rings) {
      ctx.globalAlpha = Math.max(0, 1 - r.r / r.max) * 0.85;
      ctx.strokeStyle = r.color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    this.particles.draw(ctx);

    // Vignette.
    const vig = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.34, W / 2, H / 2, Math.max(W, H) * 0.78);
    vig.addColorStop(0, 'rgba(0,0,0,0)');
    vig.addColorStop(1, 'rgba(0,0,0,0.5)');
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, W, H);
  }

  /** Mottled rock texture + a couple of diagonal cracks. */
  private drawRock(ctx: CanvasRenderingContext2D, depth: number, y: number, bandH: number, color: string, glow: number): void {
    const W = this.w;
    const base = depth * 31.7;
    ctx.save();
    // Mottling blotches (darker rock).
    for (let i = 0; i < 14; i++) {
      const rx = hash(base + i) * W;
      const ry = y + hash(base + i + 50) * bandH;
      const rr = 6 + hash(base + i + 99) * 16;
      ctx.globalAlpha = 0.05 + 0.05 * glow;
      ctx.fillStyle = hash(base + i + 7) > 0.5 ? '#000' : this.shade(color, 0.7);
      ctx.beginPath();
      ctx.ellipse(rx, ry, rr, rr * 0.6, hash(base + i) * 3.14, 0, Math.PI * 2);
      ctx.fill();
    }
    // Cracks.
    ctx.globalAlpha = 0.16;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      const sx = hash(base + i + 13) * W;
      ctx.beginPath();
      ctx.moveTo(sx, y + 4);
      ctx.lineTo(sx + (hash(base + i + 21) - 0.5) * 40, y + bandH * 0.5);
      ctx.lineTo(sx + (hash(base + i + 33) - 0.5) * 70, y + bandH - 4);
      ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  /** Glowing ore speckle veins. */
  private drawVeins(ctx: CanvasRenderingContext2D, depth: number, y: number, bandH: number, color: string, glow: number): void {
    const W = this.w;
    const seed = depth * 9301;
    ctx.save();
    if (glow > 0.05) { ctx.shadowColor = color; ctx.shadowBlur = 8 * glow; }
    ctx.fillStyle = this.shade(color, 0.95);
    for (let i = 0; i < 18; i++) {
      const px = hash(seed + i) * W;
      const py = y + hash(seed + i + 40) * bandH;
      const tw = 0.35 + glow * (0.4 + 0.4 * Math.sin(this.t * 3 + i));
      ctx.globalAlpha = Math.min(1, tw);
      ctx.beginPath();
      ctx.arc(px, py, 1.4 + glow * 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  /** Faceted gem crystals embedded in the focused layer, twinkling. */
  private drawGems(ctx: CanvasRenderingContext2D, depth: number, y: number, bandH: number, color: string, glow: number): void {
    const W = this.w;
    const seed = depth * 4567 + 17;
    const shaft = W / 2;
    for (let i = 0; i < 6; i++) {
      const gx = 24 + hash(seed + i) * (W - 48);
      if (Math.abs(gx - shaft) < 22) continue; // avoid the shaft
      const gy = y + bandH * (0.28 + hash(seed + i + 11) * 0.5);
      const r = 4 + hash(seed + i + 22) * 4;
      const tw = 0.55 + 0.45 * Math.sin(this.t * 2.5 + i * 1.7);
      ctx.save();
      ctx.translate(gx, gy);
      ctx.globalAlpha = glow * (0.6 + 0.4 * tw);
      ctx.shadowColor = color;
      ctx.shadowBlur = 12 * glow * tw;
      // Diamond facet.
      ctx.beginPath();
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.8, -r * 0.2);
      ctx.lineTo(0, r);
      ctx.lineTo(-r * 0.8, -r * 0.2);
      ctx.closePath();
      ctx.fillStyle = this.shade(color, 0.95);
      ctx.fill();
      // Bright top facet.
      ctx.beginPath();
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.8, -r * 0.2);
      ctx.lineTo(0, -r * 0.1);
      ctx.lineTo(-r * 0.8, -r * 0.2);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.fill();
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /** A rounded pill behind the ore label for readability. */
  private drawLabel(ctx: CanvasRenderingContext2D, icon: string, name: string, cy: number, focused: boolean): void {
    const text = `${icon} ${name}`;
    ctx.font = `${focused ? '700 ' : '500 '}13px Inter, sans-serif`;
    const wText = ctx.measureText(text).width;
    const padX = 8;
    const x = 10;
    const w = wText + padX * 2;
    const h = 22;
    ctx.fillStyle = focused ? 'rgba(255,182,72,0.16)' : 'rgba(0,0,0,0.28)';
    this.roundRect(ctx, x, cy - h / 2, w, h, 6);
    ctx.fill();
    if (focused) {
      ctx.strokeStyle = 'rgba(255,182,72,0.5)';
      ctx.lineWidth = 1;
      this.roundRect(ctx, x, cy - h / 2, w, h, 6);
      ctx.stroke();
    }
    ctx.fillStyle = focused ? '#fff' : 'rgba(230,237,246,0.7)';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + padX, cy + 1);
  }

  /** Chunky reciprocating drill with a housing, rotating bit and heat glow. */
  private drawDrill(ctx: CanvasRenderingContext2D, y: number, bandH: number, active: boolean, color: string): void {
    const cx = this.w / 2;
    const topY = y + 6;
    const reach = bandH * 0.42;
    const punch = active ? (Math.sin(this.t * 14) * 0.5 + 0.5) * reach : reach * 0.25;
    const bitY = topY + bandH * 0.18 + punch;

    // Housing/motor at the top.
    ctx.fillStyle = '#3a465c';
    this.roundRect(ctx, cx - 9, topY - 6, 18, 14, 3); ctx.fill();
    ctx.fillStyle = '#566380';
    this.roundRect(ctx, cx - 9, topY - 6, 18, 4, 2); ctx.fill();

    // Rod.
    ctx.strokeStyle = '#aab6cc';
    ctx.lineWidth = 5;
    ctx.beginPath(); ctx.moveTo(cx, topY + 6); ctx.lineTo(cx, bitY); ctx.stroke();
    ctx.strokeStyle = '#7e8aa3';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(cx, topY + 6); ctx.lineTo(cx, bitY); ctx.stroke();

    // Rotating auger bit (segmented triangle that flips with spin).
    const spin = active ? Math.sin(this.t * 22) : 1;
    ctx.fillStyle = '#e6edf6';
    ctx.beginPath();
    ctx.moveTo(cx - 7 * Math.abs(spin || 0.2), bitY);
    ctx.lineTo(cx + 7 * Math.abs(spin || 0.2), bitY);
    ctx.lineTo(cx, bitY + 16);
    ctx.closePath();
    ctx.fill();
    // Bit edge highlight.
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(cx, bitY + 2); ctx.lineTo(cx, bitY + 14); ctx.stroke();

    if (active) {
      // Heat glow at the tip.
      const heat = ctx.createRadialGradient(cx, bitY + 16, 0, cx, bitY + 16, 14);
      heat.addColorStop(0, 'rgba(255,180,90,0.5)');
      heat.addColorStop(1, 'rgba(255,180,90,0)');
      ctx.fillStyle = heat;
      ctx.fillRect(cx - 16, bitY, 32, 32);
      // Sparks.
      if (Math.sin(this.t * 14) > 0.6) {
        for (let i = 0; i < 4; i++) {
          const a = Math.random() * Math.PI - Math.PI / 2;
          ctx.globalAlpha = 0.85;
          ctx.fillStyle = i % 2 ? color : '#fff3d6';
          const sx = cx + Math.cos(a) * (5 + Math.random() * 10);
          const sy = bitY + 16 + Math.sin(a) * (4 + Math.random() * 8);
          ctx.fillRect(sx, sy, 2, 2);
        }
        ctx.globalAlpha = 1;
      }
    }
  }

  /** Little hard-hat miners swinging pickaxes along the band floor. */
  private drawDiggers(ctx: CanvasRenderingContext2D, y: number, bandH: number, active: boolean): void {
    const floor = y + bandH - 8;
    for (const dg of this.diggers) {
      if (active) dg.phase += 0.07 * dg.speed;
      const px = 22 + dg.x * (this.w - 44);
      if (Math.abs(px - this.w / 2) < 20) continue; // don't overlap the shaft
      const facing = px < this.w / 2 ? 1 : -1; // face the shaft
      this.drawMiner(ctx, px, floor, dg.phase, facing, active);
    }
  }

  private drawMiner(ctx: CanvasRenderingContext2D, x: number, footY: number, phase: number, facing: number, active: boolean): void {
    const swing = active ? Math.sin(phase) * 0.6 : -0.2;
    ctx.save();
    ctx.translate(x, footY);
    ctx.scale(facing, 1);

    // Legs.
    ctx.strokeStyle = '#2c3550';
    ctx.lineWidth = 2.4;
    ctx.beginPath(); ctx.moveTo(-1.5, -6); ctx.lineTo(-2.5, 0); ctx.moveTo(1.5, -6); ctx.lineTo(2.5, 0); ctx.stroke();
    // Body / overalls.
    ctx.fillStyle = '#3a6ea5';
    this.roundRect(ctx, -3.5, -16, 7, 11, 2); ctx.fill();
    // Arm + pickaxe (swings).
    ctx.save();
    ctx.translate(1, -13);
    ctx.rotate(-0.5 + swing);
    ctx.strokeStyle = '#caa26a'; // wooden handle
    ctx.lineWidth = 1.8;
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(9, 0); ctx.stroke();
    ctx.strokeStyle = '#cfd8e3'; // metal head
    ctx.lineWidth = 2.2;
    ctx.beginPath(); ctx.moveTo(8, -3); ctx.lineTo(11, 3); ctx.stroke();
    ctx.restore();
    // Head.
    ctx.fillStyle = '#e6b98a';
    ctx.beginPath(); ctx.arc(0, -18, 3.2, 0, Math.PI * 2); ctx.fill();
    // Helmet.
    ctx.fillStyle = '#ffcf3f';
    ctx.beginPath(); ctx.arc(0, -19, 3.6, Math.PI, 0); ctx.fill();
    ctx.fillRect(-3.8, -19, 7.6, 1.4);
    // Lamp glow.
    ctx.save();
    ctx.shadowColor = '#fff6c0';
    ctx.shadowBlur = active ? 6 : 2;
    ctx.fillStyle = '#fff6c0';
    ctx.beginPath(); ctx.arc(2.4, -20, 1.1, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    ctx.restore();
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /** Periodic minecart that travels up/down the shaft. */
  private updateCart(dt: number, maxDepth: number): void {
    const bottom = (maxDepth + 1) * (this.h / Math.max(1, maxDepth + 1));
    if (!this.cart.active) {
      if (this.t >= this.cart.nextAt) {
        this.cart.active = true;
        this.cart.dir = Math.random() < 0.5 ? 1 : -1;
        this.cart.y = this.cart.dir === 1 ? -16 : bottom + 16;
      }
      return;
    }
    this.cart.y += this.cart.dir * 90 * dt;
    if ((this.cart.dir === 1 && this.cart.y > bottom + 16) || (this.cart.dir === -1 && this.cart.y < -16)) {
      this.cart.active = false;
      this.cart.nextAt = this.t + 6 + Math.random() * 10;
    }
  }

  private drawCart(ctx: CanvasRenderingContext2D, cx: number, y: number): void {
    ctx.save();
    ctx.translate(cx, y);
    // Body.
    ctx.fillStyle = '#5a4636';
    this.roundRect(ctx, -8, -5, 16, 9, 2); ctx.fill();
    ctx.fillStyle = '#7a604a';
    this.roundRect(ctx, -8, -5, 16, 3, 2); ctx.fill();
    // Ore load.
    ctx.fillStyle = '#9aa7b8';
    ctx.beginPath(); ctx.arc(-3, -5, 2, 0, Math.PI * 2); ctx.arc(2, -6, 2.2, 0, Math.PI * 2); ctx.fill();
    // Wheels.
    ctx.fillStyle = '#2c3550';
    ctx.beginPath(); ctx.arc(-5, 5, 2.2, 0, Math.PI * 2); ctx.arc(5, 5, 2.2, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  /** Occasional water drips that fall and splash. */
  private updateDrips(dt: number, maxDepth: number): void {
    const bandH = this.h / Math.max(1, maxDepth + 1);
    if (Math.random() < dt * 0.7 * this.density && this.drips.length < 12) {
      const depth = Math.floor(Math.random() * (maxDepth + 1));
      const x = 20 + Math.random() * (this.w - 40);
      if (Math.abs(x - this.w / 2) > 18) {
        this.drips.push({ x, y: depth * bandH + 2, vy: 0, floor: (depth + 1) * bandH - 4 });
      }
    }
    for (const d of this.drips) { d.vy += 220 * dt; d.y += d.vy * dt; }
    for (const d of this.drips) {
      if (d.y >= d.floor) this.rings.push({ x: d.x, y: d.floor, r: 1, max: 8, color: 'rgba(150,200,255,0.7)' });
    }
    this.drips = this.drips.filter((d) => d.y < d.floor);
  }

  private drawDrips(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = 'rgba(150, 200, 255, 0.6)';
    for (const d of this.drips) {
      ctx.beginPath();
      ctx.ellipse(d.x, d.y, 1.2, 2.6, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /** Mix a hex color toward darkness by factor (0 dark .. 1 full). */
  private shade(hex: string, factor: number): string {
    const c = hex.replace('#', '');
    const n = c.length === 3
      ? c.split('').map((ch) => parseInt(ch + ch, 16))
      : [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
    const [r, g, b] = n as [number, number, number];
    return `rgb(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)})`;
  }
}
